import {Shell} from "@client/components/Shell";
import {BUTTON_CLASS} from "@client/components/styles";
import * as analytics from "@client/shared/analytics";
import {Static} from "@client/shared/components/Static";
import {useStaticURL} from "@client/site/context";
import {CheckIcon, XMarkIcon} from "@heroicons/react/24/solid";
import {server} from "@reactivated";
import React, {useRef, useState} from "react";

function Logo() {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 73 26" fill="none">
            <g id="Group 83">
                <path
                    id="J"
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M16.9549 0.265625H13.5602V16.1073H13.5786C13.5786 18.9296 11.2906 21.2176 8.46827 21.2176C5.64592 21.2176 3.35796 18.9296 3.35796 16.1073H0C0 20.7842 3.79137 24.5756 8.46827 24.5756C13.1452 24.5756 16.9365 20.7842 16.9365 16.1073H16.9549V0.265625Z"
                    fill="white"
                />
                <g id="O">
                    <path
                        id="Subtract"
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M40.5045 5.11579L42.5968 3.02356L42.5705 3.00203C42.3434 2.81564 42.1095 2.63708 41.8693 2.46677C39.8294 1.02031 37.3359 0.169922 34.6465 0.169922C27.7429 0.169922 22.1465 5.76636 22.1465 12.6699C22.1465 19.5735 27.7429 25.1699 34.6465 25.1699C41.55 25.1699 47.1465 19.5735 47.1465 12.6699C47.1465 11.2074 46.8949 9.80166 46.432 8.49483C46.3185 8.17443 46.1923 7.86004 46.054 7.5523L46.0246 7.48685L43.7541 9.7574C44.0467 10.6734 44.2053 11.6513 44.2053 12.6699C44.2053 17.9491 39.9257 22.2287 34.6465 22.2287C29.3673 22.2287 25.0877 17.9491 25.0877 12.6699C25.0877 7.39073 29.3673 3.1111 34.6465 3.1111C36.7066 3.1111 38.6091 3.76052 40.168 4.86599C40.2821 4.94684 40.3942 5.03013 40.5045 5.11579Z"
                        fill="white"
                    />
                    <path
                        id="Vector 1 (Stroke)"
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M45.9055 6.04593L35.153 16.7985L31.1484 12.7939L33.5342 10.4082L35.153 12.0269L43.5198 3.66016L45.9055 6.04593Z"
                        fill="#EF8165"
                    />
                </g>
                <path
                    id="Y"
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M49.2043 0.266418L59.0605 17.3378V25.1598H62.4551V17.5304L72.4225 0.266418H68.5027L60.8134 13.5846L53.1241 0.266418H49.2043Z"
                    fill="white"
                />
            </g>
        </svg>
    );
}

export function Template(props: server.journal.views.PullUp) {
    const STATIC_URL = useStaticURL();
    analytics.useTrackPageView();

    React.useEffect(() => {
        void analytics.track("PULL_UP_VIEW", {
            pull_up_experiment_name: props.pull_up_experiment_name,
        });
    }, []);

    const [isOpen, setIsOpen] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const dialogRef = useRef<HTMLDialogElement>(null);

    const handleOpen = () => {
        dialogRef.current?.showModal();
        setIsOpen(true);
        setIsSubmitted(false);
        void analytics.track("CLICKED_PULL_UP_SIGN_UP", {
            pull_up_experiment_name: props.pull_up_experiment_name,
        });
    };

    const handleClose = () => {
        setIsOpen(false);
        setIsSubmitted(false);
        void analytics.track("CLOSED_PULL_UP_SIGN_UP", {
            pull_up_experiment_name: props.pull_up_experiment_name,
        });
    };

    const v1 = (
        <>
            <div className="absolute inset-0 bg-black/20" />
            <div className="relative z-10 flex h-full flex-col text-center">
                <div className="*:mx-auto *:w-20">
                    <Logo />
                </div>
                <div className="flex flex-1 flex-col justify-between gap-8 py-10">
                    <h1 className="text-5xl font-extralight text-white/95">
                        The Pull Up
                    </h1>
                    <p className="text-xl font-light text-white">
                        Most people can’t do one.
                    </p>
                    <p className="text-xl font-light text-white">
                        Our customized program will help you get there.
                    </p>
                    <p className="text-xl font-light text-white">
                        Can’t even hang from the bar?
                        <br />
                        Our program handles it.
                    </p>
                    <p className="text-xl font-light text-white">
                        Getting partial reps?
                        <br />
                        Our program fixes it.
                    </p>
                    <button className={BUTTON_CLASS} onClick={handleOpen}>
                        Sign Up
                    </button>
                </div>
            </div>
        </>
    );

    const v2 = (
        <>
            <div className="absolute inset-0 bg-black/40" />
            <div className="relative z-10 mx-auto flex h-full max-w-md flex-col justify-between text-white">
                <div className="mb-6 w-16">
                    <Logo />
                </div>
                <div className="mb-4">
                    <h1 className="mb-4 text-4xl/5 font-extralight">
                        Can’t Do a Pull Up?
                    </h1>
                    <h2 className="mb-2 text-2xl font-extralight">
                        You’re closer than you think.
                    </h2>
                </div>

                <div className="mb-4">
                    <ul className="list-none space-y-1">
                        <li className="flex items-center">
                            <XMarkIcon className="mr-2 size-5 text-purple-400" />
                            Can’t hang from the bar?
                        </li>
                        <li className="flex items-center">
                            <XMarkIcon className="mr-2 size-5 text-purple-400" />
                            Getting stuck halfway up?
                        </li>
                        <li className="flex items-center">
                            <XMarkIcon className="mr-2 size-5 text-purple-400" />
                            Always relying on assisted pull-ups?
                        </li>
                    </ul>
                </div>
                <p className="mb-2 text-2xl font-extralight">
                    We’ll fix it. Our program has:
                </p>

                <div className="mb-6">
                    <ul className="list-none space-y-1">
                        <li className="flex items-center">
                            <CheckIcon className="mr-2 size-5 text-purple-400" />
                            Smart weekly progressions
                        </li>
                        <li className="flex items-center">
                            <CheckIcon className="mr-2 size-5 text-purple-400" />
                            Minimal equipment needed
                        </li>
                        <li className="flex items-center">
                            <CheckIcon className="mr-2 size-5 text-purple-400" />
                            No guesswork. Just follow the plan
                        </li>
                    </ul>
                </div>

                <div className="mb-8 border-l-2 border-purple-400 pl-4 text-base text-white italic">
                    I never thought I could do a pull up.
                    <br />
                    Now I can do three. Unassisted.
                    <br />
                    <span className="text-sm not-italic">Sarah M, age 34</span>
                </div>

                <button className={BUTTON_CLASS} onClick={handleOpen}>
                    {props.pull_up_experiment_name === "v1"
                        ? "Sign Up"
                        : props.pull_up_experiment_name === "v2_a"
                          ? "Start Free"
                          : "Try It Free"}
                </button>
                <div className="text-center text-sm font-light text-gray-100">
                    Built by experts. Proven with real users.
                </div>
            </div>
        </>
    );

    const currentPageContent = props.pull_up_experiment_name === "v1" ? v1 : v2;

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const formData = new FormData(e.currentTarget);
        const email = formData.get("email") as string;
        void analytics.track("REGISTERED_PULL_UP", {
            registration_email: email,
            pull_up_experiment_name: props.pull_up_experiment_name,
        });
        e.currentTarget.reset();
        setIsSubmitted(true);
    };

    const submissionPrompt =
        props.pull_up_experiment_name === "v2_c" ? (
            <>
                <h2 className="text-3xl font-light">Get the free program</h2>
                <ul className="list-none space-y-1">
                    <li className="flex items-center">
                        <CheckIcon className="mr-2 size-5 text-purple-400" />
                        Step-by-step beginner plan
                    </li>
                    <li className="flex items-center">
                        <CheckIcon className="mr-2 size-5 text-purple-400" />
                        Minimal equipment required
                    </li>
                    <li className="flex items-center">
                        <CheckIcon className="mr-2 size-5 text-purple-400" />
                        Totall free
                    </li>
                </ul>
                {/* Form for v2_c, identical to the other for now */}
                <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                    <input
                        type="email"
                        name="email"
                        placeholder="Enter your email"
                        required
                        className="w-full rounded-lg border border-white/20 bg-white/10 px-4 py-3 text-white placeholder:text-white/50 focus:border-white/40 focus:outline-hidden"
                    />
                    <button type="submit" className={` ${BUTTON_CLASS} w-full`}>
                        Send it now
                    </button>
                </form>
            </>
        ) : (
            <>
                <h2 className="text-center text-2xl font-light">Get the program</h2>
                <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                    <input
                        type="email"
                        name="email"
                        placeholder="Enter your email"
                        required
                        className="w-full rounded-lg border border-white/20 bg-white/10 px-4 py-3 text-white placeholder:text-white/50 focus:border-white/40 focus:outline-hidden"
                    />
                    <button type="submit" className={` ${BUTTON_CLASS} w-full`}>
                        Submit
                    </button>
                </form>
            </>
        );
    return (
        <Shell>
            <Static />
            <meta charSet="UTF-8" />
            <title>Pull Up Guide | Joy Health Tracker</title>
            <meta
                name="viewport"
                content="viewport-fit=cover, width=device-width, initial-scale=1.0, minimum-scale=1.0, maximum-scale=1.0, user-scalable=no"
            />
            <link rel="icon" href={`${STATIC_URL}favicon.png`} type="image/png" />
            <style>
                {`
                body {
                    background-color: #000;
                }
                `}
            </style>
            <div
                className="m-auto h-dvh max-h-[1000px] w-dvw max-w-(--breakpoint-md) bg-cover bg-center p-8"
                style={{backgroundImage: `url(${STATIC_URL}images/pull-up.jpg)`}}
            >
                {currentPageContent}
            </div>
            <dialog
                ref={dialogRef}
                className={`mx-auto w-full max-w-md rounded-lg border border-white/10 bg-black/90 p-6 text-white shadow-xl backdrop:bg-black/50 backdrop:backdrop-blur-xs ${isOpen ? "animate-fade-in" : "animate-fade-out"} `}
                onClose={handleClose}
            >
                <div className="flex flex-col gap-4">
                    {!isSubmitted ? (
                        submissionPrompt
                    ) : (
                        <div className="text-center text-pretty">
                            <h2 className="mb-4 text-2xl font-light">
                                You’re on the list!
                            </h2>
                            <p className="mb-6 text-white/80">
                                Due to high demand, we’ve limited the number of people
                                who can join the program.
                            </p>
                            <p className="mb-6 text-white/80">
                                We’ll be in touch when the next spot opens up.
                            </p>
                        </div>
                    )}
                    <button
                        onClick={() => dialogRef.current?.close()}
                        className="text-sm text-white/70 hover:text-white"
                    >
                        Close
                    </button>
                </div>
            </dialog>
        </Shell>
    );
}
